import * as queryString from 'query-string';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import cookie from 'react-cookies';
import { useDispatch, useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { OriginArea } from '@videoblocks/events-ts/lib/storyblocks/search/OriginAreaEnum';
import { Telemetry } from '@videoblocks/kafka-rest-client';
import { LegacyVideo as Video } from '@videoblocks/storywind';

import { setPendingPauseAudio } from '../../../../Audio/actions/AudioActions';
import displayEventMediator from '../../../../Events/DisplayEventMediator';
import events from '../../../../Events/Events';
import StockItemDisplayEventFactory from '../../../../Events/StockItemDisplayEventFactory';
import StockItemHoverEventFactory from '../../../../Events/StockItemHoverEventFactory';
import { STORYBOARD_REQUEST_ID_COOKIE } from '../../../../Storyboards/constants';
import {
  selectHasVideoSubscription,
  selectIsLoggedIn,
  selectIsMobile,
  selectPrimarySubscription,
  selectUseNewDownloadOverlay,
} from '../../../../auth/AuthSelectors';
import { contentTypes } from '../../../../common/Constants';
import ElementDisplayedListener from '../../../../common/ElementDisplayedListener';
import Logger from '../../../../common/Logger';
import SiteConstants from '../../../../common/SiteConstants/SiteConstants';
import Duration from '../../../../common/components/Duration';
import LoadingIndicator from '../../../../common/components/LoadingIndicator';
import { StockDownloadContext } from '../../../../common/context/StockDownloadContext';
import {
  StockItem,
  StockItemContext,
  StockItemFormat,
} from '../../../../common/types/StockItemTypes';
import {
  convertStockItemContextToOriginArea,
  getPreviewUrl,
  isMouseOverElement,
  sendBeacon,
  stopRecommendedMusicDrawerTimer,
} from '../../../../common/utils';
import {
  selectIsPolling,
  openVideoEditorModal,
  selectAIEditButtonVisible,
  selectShouldShowAIEditFeatures,
} from '../../../ContentEdit/ContentEditSlice';
import { DrawerType } from '../../SearchTypes';
import {
  collapseMoreLikeThisDrawer,
  expandMoreLikeThisDrawer,
  setSelectedStockItemOptions,
  updateSearchPage,
} from '../../actions/SearchActions';
import {
  selectDrawerIsOpen,
  selectDrawerSelectedItemId,
  selectDrawerType,
  selectSearchFeatures,
  selectSelectedStockItem,
} from '../../selectors/searchSelectors';
import StockItemCardButtons from './StockItemCardButtons';
import StockItemIcons from './StockItemIcons';
import VideoStockItemFormatSelector from './VideoStockItemFormatSelector';
import {
  AIEditButton,
  DownloadButton,
  FavoriteButton,
  MakerButton,
  RelatedVideosButtonContainer,
} from './components/Buttons';
import StockItemActions from './components/StockItemActions';
import VideoStockItemDownloader from './components/VideoStockItemDownloader';

export type VideoStockItemCardProps = {
  binUniqueId: string;
  classNames: string[];
  containerResponsiveClassNames: string[];
  containPoppers: boolean;
  context: StockItemContext;
  draggableCard: boolean;
  handleLoadingMoreCollapsedStockItems: () => void;
  hideOnThreeColumnLayout: boolean;
  isCollapsedStockItemLoadMoreCard: boolean;
  isSelected: boolean;
  page: number;
  searchLogParameters: object;
  shouldInvertControlsVisibility: boolean;
  shouldShowAddToFavorites: boolean;
  shouldShowDeleteButton: boolean;
  shouldShowDownloadButton: boolean;
  shouldStockItemHaveNewFlag: boolean;
  shouldShowMakerButton: boolean;
  stockItem: StockItem;
  stockItemCardContext?: { title: string; position: number };
  stockItemFormats: StockItemFormat[];
  style: object;
  thumbnailClassNames: string;
  overlayGradientClassNames?: string;
  downloadButtonTelemetryAction: () => void;
  makerButtonTelemetryAction: () => void;
  favoriteButtonTelemetryAction: () => void;
  openDetailsInNewWindow: boolean;
  openFoldersInNewWindow: boolean;
  redirectToSignUpLink: boolean;
  clickOriginArea?: OriginArea;
  isFocusOutlineVisible?: boolean;
  isVertical?: boolean;
};

const VideoStockItemCard = ({
  isCollapsedStockItemLoadMoreCard = false,
  stockItem,
  style = {},
  draggableCard,
  page,
  stockItemCardContext,
  context,
  stockItemFormats,
  shouldInvertControlsVisibility = false,
  shouldShowDownloadButton = true,
  downloadButtonTelemetryAction = null,
  makerButtonTelemetryAction = null,
  containPoppers = false,
  openFoldersInNewWindow = false,
  favoriteButtonTelemetryAction,
  shouldStockItemHaveNewFlag,
  thumbnailClassNames = '',
  overlayGradientClassNames = '',
  shouldShowMakerButton: shouldShowMakerButtonProp = true,
  isSelected,
  containerResponsiveClassNames = [
    'relative',
    'float-left',
    'md:w-1/3',
    'lg:w-1/4',
    'w-5/6',
  ],
  classNames = [],
  hideOnThreeColumnLayout,
  redirectToSignUpLink,
  searchLogParameters,
  openDetailsInNewWindow = false,
  clickOriginArea,
  isFocusOutlineVisible,
  handleLoadingMoreCollapsedStockItems,
  isVertical = false,
}: VideoStockItemCardProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<any>(null);

  const stockDownloadContext = useContext(StockDownloadContext);

  // Redux hooks
  const dispatch = useDispatch();

  // Redux state selectors
  const selectedStockItem = useSelector(selectSelectedStockItem);
  const features = useSelector(selectSearchFeatures);
  const isStockItemSelected = selectedStockItem.id === stockItem.id;
  const isMobile = useSelector(selectIsMobile);
  const formatSelectorVisible = isStockItemSelected
    ? selectedStockItem.formatSelectorVisible
    : false;
  const selectedFormatName = isStockItemSelected
    ? selectedStockItem.selectedFormatName
    : null;
  const selectedFormatResolution = isStockItemSelected
    ? selectedStockItem.selectedFormatResolution
    : null;
  const drawerIsOpen = useSelector(selectDrawerIsOpen);
  const drawerSelectedItemId = useSelector(selectDrawerSelectedItemId);
  const drawerType = useSelector(selectDrawerType);
  const downloadPermissions = features.downloadPermissions;
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const videoSubscription = useSelector(selectHasVideoSubscription);
  const primarySubscription = useSelector(selectPrimarySubscription);
  const useNewDownloadOverlay = useSelector(selectUseNewDownloadOverlay);
  const allowedToSeeAIEditButton = useSelector(selectAIEditButtonVisible);
  const isContentEditPolling = useSelector(selectIsPolling);
  const shouldShowAIEditFeatures = useSelector(selectShouldShowAIEditFeatures);

  const [accumulatedPlayMs, setAccumulatedPlayMs] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isLoggerExecuted, setIsLoggerExecuted] = useState<boolean>(false);
  const [playPromiseProcessing, setPlayPromiseProcessing] =
    useState<boolean>(false);
  const [showMoreLikeThisTooltip, setShowMoreLikeThisTooltip] =
    useState<boolean>(false);
  const [hoverStartTime, setHoverStartTime] = useState<number | null>(null);
  const [playStartTime, setPlayStartTime] = useState<number | null>(null);
  const [cardInViewport, setCardInViewport] = useState<boolean>(false);

  // Touch drag/viewport listeners
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const handleTouchMove = () => {
      if (!isDragging) setIsDragging(true);
    };
    const handleTouchStart = () => {
      if (isDragging) setIsDragging(false);
    };

    node.addEventListener('touchmove', handleTouchMove);
    node.addEventListener('touchstart', handleTouchStart);

    shouldShowMoreLikeThisTooltipByDefault();

    // Stop recommended music drawer timer on unload
    const beforeUnloadHandler = () => {
      // @ts-ignore
      window.recommendedMusicDrawerTimer?.();
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);

    return () => {
      node.removeEventListener('touchmove', handleTouchMove);
      node.removeEventListener('touchstart', handleTouchStart);
      ElementDisplayedListener(node, () => setCardInViewport(true), 0.1);
      ElementDisplayedListener(node, () => {
        if (stockItemCardContext?.position) {
          displayEventMediator.push(
            StockItemDisplayEventFactory.displayedStockItem(
              stockItem,
              stockItemCardContext.position
            )
          );
        }
        window.removeEventListener('beforeunload', beforeUnloadHandler);
        pauseAudioAndStopRecommendedMusicDrawerTimer();
      });
    };
    // eslint-disable-next-line
  }, []);

  // Helper: play duration
  const playDurationMs = useCallback(() => {
    return playStartTime ? Date.now() - playStartTime : 0;
  }, [playStartTime]);

  // Helper: get selected format
  const getSelectedFormat = useCallback(() => {
    return stockItemFormats.length >= 2 && selectedFormatName
      ? stockItemFormats.find(
          (stockItemFormat) =>
            stockItemFormat.formatName === selectedFormatName &&
            stockItemFormat.resolution === selectedFormatResolution
        )
      : stockItemFormats[0];
  }, [stockItemFormats, selectedFormatName, selectedFormatResolution]);

  // Helper: is subscriptionless user
  const isASubscriptionlessUser = isLoggedIn && !videoSubscription;

  // Helper: get click origin area
  const getClickOriginArea = useCallback(() => {
    if (clickOriginArea) return clickOriginArea;
    switch (context) {
      case StockItemContext.SEARCH:
        return OriginArea.SEARCH;
      case StockItemContext.SIMILAR_CONTENT:
        return OriginArea.STOCK_DETAILS;
      case StockItemContext.DRAWER:
        if (drawerType === DrawerType.MORE_LIKE_THIS) {
          return OriginArea.MORE_LIKE_THIS;
        }
        break;
      default:
        return null;
    }
  }, [clickOriginArea, context, drawerType]);

  const shouldShowSimilarContent =
    stockItem.numCollapses > 2 && context === StockItemContext.SEARCH;

  const shouldShowRecommendedMusic =
    stockItem.contentType === contentTypes.FOOTAGE &&
    stockItem.hasRecommendedMusic &&
    context === StockItemContext.SEARCH;

  const shouldShowMakerButton =
    shouldShowMakerButtonProp &&
    (stockItem.contentType === 'footage' ||
      stockItem.contentType === 'motionbackground') &&
    !stockItem.is360 &&
    !isMobile;

  const shouldShowAIEditButton =
    allowedToSeeAIEditButton &&
    shouldShowAIEditFeatures &&
    stockItem.contentType === contentTypes.FOOTAGE &&
    !isVertical;

  const getContainerClassNames = useCallback(() => {
    return twMerge(
      isFocusOutlineVisible &&
        'rounded-2xl focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2',
      containerResponsiveClassNames,
      classNames,
      'stock-item stock-item-v2',
      isSelected && 'is-selected',
      formatSelectorVisible && 'download-format-selector-visible',
      hideOnThreeColumnLayout && 'hidden-sm hidden-md',
      shouldShowSimilarContent && 'collapsed-card',
      context !== StockItemContext.DRAWER &&
        context !== StockItemContext.SIMILAR_CONTENT &&
        'p-0 w-auto md:w-auto lg:w-auto 3xl:w-auto 4xl:w-auto m-0',
      context === StockItemContext.DRAWER &&
        'p-0 w-auto md:w-auto lg:w-auto 3xl:w-auto 4xl:w-auto m-0',
      context === StockItemContext.SIMILAR_CONTENT && 'px-2 mb-3'
    );
  }, [
    isFocusOutlineVisible,
    containerResponsiveClassNames,
    classNames,
    isSelected,
    formatSelectorVisible,
    hideOnThreeColumnLayout,
    context,
  ]);

  function shouldShowMoreLikeThisTooltipByDefault() {
    const hasSeenMoreLikeThisTooltip = cookie.load('morelikethis');
    const domain = window && window.location && `.${window.location.hostname}`;
    if (!hasSeenMoreLikeThisTooltip) {
      cookie.save('morelikethis', true, { path: '/', domain });
      setShowMoreLikeThisTooltip(true);
    }
    setShowMoreLikeThisTooltip(false);
  }

  const logHoverDone = useCallback(() => {
    if (hoverStartTime) {
      const hoverDuration = Date.now() - hoverStartTime;
      const playEnd = videoRef.current?.videoRef?.current?.currentTime ?? 0;
      const newAccumulatedPlayMs = accumulatedPlayMs + playDurationMs();
      const timesLooped = Math.floor(
        newAccumulatedPlayMs / 1000 / stockItem.duration
      );
      const originArea = convertStockItemContextToOriginArea(context);

      let storyboardRequestId: string | undefined;
      let storyboardSceneRequestId: string | undefined;

      if (originArea === OriginArea.STORYBOARD) {
        storyboardRequestId = cookie.load(STORYBOARD_REQUEST_ID_COOKIE);
        storyboardSceneRequestId = stockDownloadContext.sceneRequestId;
      }

      const hoverEvent = StockItemHoverEventFactory.create(
        stockItem,
        hoverStartTime,
        playStartTime,
        playEnd,
        timesLooped,
        newAccumulatedPlayMs,
        Date.now(),
        originArea,
        storyboardRequestId,
        storyboardSceneRequestId
      );

      if (hoverDuration > 1000) {
        events.produce(hoverEvent, hoverEvent.user.visitorCookieId);
      }
    }
    // eslint-disable-next-line
  }, [
    hoverStartTime,
    accumulatedPlayMs,
    playDurationMs,
    stockItem,
    context,
    playStartTime,
    stockDownloadContext,
  ]);

  const logPrimaryClickAction = useCallback(() => {
    const stockItemId = stockItem.id;
    const position = stockItemCardContext && stockItemCardContext.position;
    const urlPath = '/api/video/stockItemClickEvent/download';
    const clickOrigin = getClickOriginArea();

    const beacon = sendBeacon(urlPath, stockItemId, position, clickOrigin);

    if (!beacon) {
      Logger.produceDownloadClickEvent(stockItem, position, clickOrigin);
    }
    logHoverDone();
  }, [stockItem, stockItemCardContext, getClickOriginArea, logHoverDone]);

  const logDetailsClickAction = useCallback(() => {
    const stockItemId = stockItem.id;
    const position = stockItemCardContext && stockItemCardContext.position;
    const urlPath = '/api/video/stockItemClickEvent/details';
    const clickOrigin = getClickOriginArea();

    const beacon = sendBeacon(urlPath, stockItemId, position, clickOrigin);

    if (!beacon) {
      Logger.produceDetailsClickEvent(stockItem, position, clickOrigin);
    }
    logHoverDone();
  }, [stockItem, stockItemCardContext, getClickOriginArea, logHoverDone]);

  const primaryActionUpdateSelectedStockItemOption = useCallback(() => {
    setIsHovering(false);
    setIsPlaying(false);
    dispatch(
      setSelectedStockItemOptions({
        id: stockItem.id,
        formatSelectorVisible: true,
        selectedFormatName: stockItemFormats[0].formatName,
        selectedFormatResolution: stockItemFormats[0].resolution,
      })
    );
  }, [dispatch, stockItem, stockItemFormats]);

  const primaryActionDownloadItem = useCallback(
    async (selectedFormat) => {
      const params = { ...stockDownloadContext };
      const originArea = convertStockItemContextToOriginArea(context);
      if (originArea) {
        params['origin_area'] = originArea;
      }
      Telemetry.increment('download.clicked.stockItemCard');
      const dataBridge = await SiteConstants.buildInstance().getDataBridge();
      await dataBridge.downloadItem(
        queryString.stringifyUrl(
          {
            url: selectedFormat.downloadUrl,
            query: params,
          },
          {
            skipNull: true,
          }
        ),
        queryString.stringifyUrl(
          {
            url: selectedFormat.downloadAjaxUrl,
            query: params,
          },
          {
            skipNull: true,
          }
        ),
        searchLogParameters,
        dispatch,
        primarySubscription || videoSubscription
      );
    },
    [
      stockDownloadContext,
      context,
      searchLogParameters,
      dispatch,
      primarySubscription,
      videoSubscription,
    ]
  );

  const getDefaultPrimaryClickAction = useCallback(
    () =>
      async (event, clickedSelectedFormat = null) => {
        logPrimaryClickAction();
        if (!redirectToSignUpLink) {
          event.preventDefault();
          if (!formatSelectorVisible && stockItemFormats.length >= 2) {
            primaryActionUpdateSelectedStockItemOption();
          } else {
            const selectedFormat = clickedSelectedFormat
              ? clickedSelectedFormat
              : getSelectedFormat();
            await primaryActionDownloadItem(selectedFormat);
          }
        }
      },
    [
      logPrimaryClickAction,
      redirectToSignUpLink,
      formatSelectorVisible,
      stockItemFormats,
      primaryActionUpdateSelectedStockItemOption,
      getSelectedFormat,
      primaryActionDownloadItem,
    ]
  );

  const getPrimaryClickAction = useCallback(() => {
    if (isASubscriptionlessUser) {
      return () => {
        Telemetry.increment('preview.downloaded.stockItemCard');
        window.location.href = getPreviewUrl(
          stockItem.id,
          stockItem.contentClass
        );
      };
    }
    if (stockItem && !videoSubscription) {
      const visitorDownloadAttemptLink = stockItem.detailsUrl;
      return async (event) => {
        event.preventDefault();
        logPrimaryClickAction();
        window.location.href = visitorDownloadAttemptLink;
      };
    }
    return getDefaultPrimaryClickAction();
  }, [
    isASubscriptionlessUser,
    stockItem,
    videoSubscription,
    logPrimaryClickAction,
    getDefaultPrimaryClickAction,
  ]);

  const hideDownloadButtons = useCallback(
    (event) => {
      event.preventDefault();
      dispatch(
        setSelectedStockItemOptions({
          formatSelectorVisible: false,
        })
      );
      setIsHovering(true);
    },
    [dispatch]
  );

  const handleSelectedFormatChanged = useCallback(
    (selectedFormatName, selectedFormatResolution) => {
      dispatch(
        setSelectedStockItemOptions({
          id: stockItem.id,
          selectedFormatName,
          selectedFormatResolution,
        })
      );
    },
    [dispatch, stockItem]
  );

  const handleAIEditAction = useCallback(() => {
    if (shouldShowAIEditButton) {
      dispatch(openVideoEditorModal(stockItem));
      Telemetry.increment('ai.contentEdit.clicked.stockItemCard');
    }
  }, [dispatch, stockItem, shouldShowAIEditButton]);

  const pauseAudioAndStopRecommendedMusicDrawerTimer = useCallback(() => {
    setPendingPauseAudio()(dispatch);
    stopRecommendedMusicDrawerTimer();
  }, [dispatch]);

  const similarContentAction = useCallback(() => {
    if (
      drawerIsOpen &&
      (drawerType === DrawerType.MORE_LIKE_THIS ||
        drawerType === DrawerType.SAME_SHOOT_SAME_MODEL_SIMILAR_CLIPS) &&
      drawerSelectedItemId === stockItem.id
    ) {
      dispatch(collapseMoreLikeThisDrawer());
    } else {
      dispatch(
        expandMoreLikeThisDrawer({
          initialItemId: stockItem.id,
          drawerType: DrawerType.SAME_SHOOT_SAME_MODEL_SIMILAR_CLIPS,
        })
      );
    }
  }, [
    drawerIsOpen,
    drawerType,
    drawerSelectedItemId,
    stockItem,
    dispatch,
    stockItemCardContext,
  ]);

  // Mouse enter/leave handlers
  const handleIsHovering = useCallback(() => {
    if (!formatSelectorVisible) {
      setIsHovering(true);
      setHoverStartTime(Date.now());
      if (page) {
        updateSearchPage(page, stockItem.id)(dispatch);
      }
    }
  }, [formatSelectorVisible, page, dispatch, stockItem]);

  const handleIsNotHovering = useCallback(
    (e) => {
      const relatedTarget = e.relatedTarget;
      if (
        containerRef.current &&
        (relatedTarget instanceof Node
          ? !containerRef.current.contains(relatedTarget)
          : true)
      ) {
        setIsHovering(false);
        setPlayStartTime(null);
        setAccumulatedPlayMs((prev) => prev + playDurationMs());
        logHoverDone();
      }
    },
    [playDurationMs, logHoverDone]
  );

  const pause = useCallback(() => {
    if (videoRef.current?.videoRef?.current && !playPromiseProcessing) {
      videoRef.current.videoRef.current.pause();
    }
  }, [playPromiseProcessing]);

  // Play/pause video
  const play = useCallback(() => {
    if (videoRef.current.videoRef?.current) {
      const playPromise = videoRef.current.videoRef.current.play();
      setPlayPromiseProcessing(true);
      if (playPromise) {
        playPromise
          .then(() => {
            setPlayPromiseProcessing(false);
            if (!isHovering) {
              pause();
            }
          })
          .catch((e) => {
            setPlayPromiseProcessing(false);
            if (!(e instanceof DOMException && e.name === 'AbortError')) {
              if (isHovering) {
                play();
              }
            }
          });
      }
    }
  }, [isHovering, pause]);

  // Handle play/pause based on hovering state
  useEffect(() => {
    if (isHovering && !isMobile) {
      play();
    } else if (!isHovering) {
      pause();
    }
  }, [isHovering, isMobile, play, pause]);

  // Video started playing
  const handleVideoStartedPlaying = useCallback(() => {
    setIsPlaying(true);
    setPlayStartTime((prev) => prev || Date.now());
  }, []);

  // Handle click action (for favorite, etc)
  const handleClickAction = useCallback(
    (e) => {
      if (!isMouseOverElement(e?.nativeEvent || e, containerRef.current)) {
        handleIsNotHovering(e);
      }
    },
    [handleIsNotHovering]
  );

  // Handle stock item click (for details)
  const handleStockItemClick = useCallback(
    (e) => {
      e.stopPropagation();
      const nodeName = e.target.nodeName;
      if (nodeName === 'VIDEO' && !isLoggerExecuted) {
        setIsLoggerExecuted(true);
        logDetailsClickAction();
      }
    },
    [isLoggerExecuted, logDetailsClickAction]
  );

  // Touch end for details link
  const handleOnTouchEnd = useCallback(() => {
    if (!isDragging) {
      window.location.href = stockItem.detailsUrl;
    }
  }, [isDragging, stockItem]);

  // Renderers
  const renderStockItemFormatSelectorThumbnailExperiment = useCallback(
    () => (
      <VideoStockItemDownloader
        stockItemFormats={stockItemFormats}
        onClose={hideDownloadButtons}
        onSelect={async (event, format) => {
          handleSelectedFormatChanged(format.formatName, format.resolution);
          await getPrimaryClickAction()(event, format);
          hideDownloadButtons(event);
        }}
      />
    ),
    [
      stockItemFormats,
      hideDownloadButtons,
      handleSelectedFormatChanged,
      getPrimaryClickAction,
    ]
  );

  const renderStockItemFormatSelector = useCallback(
    () => (
      <VideoStockItemFormatSelector
        isVisible={formatSelectorVisible}
        stockItemFormats={stockItemFormats}
        selectedFormatName={selectedFormatName}
        selectedFormatResolution={selectedFormatResolution}
        selectedFormatChanged={handleSelectedFormatChanged}
        downloadPermissions={downloadPermissions}
      />
    ),
    [
      formatSelectorVisible,
      stockItemFormats,
      selectedFormatName,
      selectedFormatResolution,
      handleSelectedFormatChanged,
      downloadPermissions,
    ]
  );

  const renderStockItemTitle = useCallback(() => {
    const { duration, title } = stockItem;
    const hoverStyle = shouldInvertControlsVisibility
      ? 'opacity-100'
      : 'opacity-0';
    const nonHoverStyle = shouldInvertControlsVisibility
      ? 'opacity-0'
      : 'opacity-100';

    let widthClass: string;
    if (shouldShowSimilarContent && shouldShowRecommendedMusic) {
      widthClass = 'w-3/5';
    } else if (shouldShowSimilarContent || shouldShowRecommendedMusic) {
      widthClass = 'w-5/6';
    } else {
      widthClass = 'w-full';
    }

    return (
      <div
        className={twMerge(
          'item-title flex flex-wrap',
          widthClass,
          isHovering ? hoverStyle : nonHoverStyle
        )}
      >
        {duration && <Duration seconds={duration} />}
        <h3 className="truncate text-sm font-semibold video-card-redesign-title w-full mb-0 mr-4">
          {title}
        </h3>
      </div>
    );
  }, [stockItem, shouldInvertControlsVisibility, isHovering]);

  const renderTitle = useCallback(() => {
    const { duration, title } = stockItem;
    const hoverStyle = shouldInvertControlsVisibility
      ? 'opacity-0'
      : 'opacity-100';
    const nonHoverStyle = shouldInvertControlsVisibility
      ? 'opacity-100'
      : 'opacity-0';

    return (
      <div
        className={twMerge(
          `stock-item-title flex space-x-2 items-center absolute top-0 left-0 px-4 pt-4 w-full pointer-events-none`,
          isHovering ? hoverStyle : nonHoverStyle
        )}
      >
        <div
          className={
            'py-0.5 px-1.5 bg-white rounded text-sm leading-5 text-gray-900'
          }
        >
          <Duration seconds={duration} />
        </div>
        <h3 className="truncate font-semibold text-white m-0 text-base leading-5">
          {title}
        </h3>
      </div>
    );
  }, [stockItem, shouldInvertControlsVisibility, isHovering]);

  const renderVideo = useCallback(
    () => (
      <Video
        ref={videoRef}
        videoUrl={stockItem.previewUrl}
        poster={stockItem.thumbnailUrl}
        onStartedPlaying={handleVideoStartedPlaying}
        preload={false}
        className="object-fill h-full"
      />
    ),
    [stockItem, handleVideoStartedPlaying]
  );

  const renderVideoWithDetailsLink = useCallback(() => {
    const src = stockDownloadContext.sceneRequestId
      ? `${stockItem.detailsUrl}?sceneRequestId=${stockDownloadContext.sceneRequestId}`
      : stockItem.detailsUrl;
    return (
      <a
        className="image-link"
        href={src}
        onTouchEnd={handleOnTouchEnd}
        aria-label={`Go to video details for ${stockItem.title}`}
        target={openDetailsInNewWindow ? '_blank' : '_self'}
        onClick={handleStockItemClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            handleStockItemClick(event);
          }
        }}
        tabIndex={0}
      >
        <div className="overflow-hidden md:rounded-3xl">{renderVideo()}</div>
      </a>
    );
  }, [
    stockDownloadContext,
    stockItem,
    handleOnTouchEnd,
    openDetailsInNewWindow,
    handleStockItemClick,
    renderVideo,
  ]);

  const renderDownloadCancelButtons = useCallback(() => {
    if (!formatSelectorVisible || !shouldShowDownloadButton) return null;
    return (
      <div className="flex absolute left-0 bottom-0 w-full h-14">
        <StockItemCardButtons
          onPrimaryActionClicked={getPrimaryClickAction()}
          onSecondaryActionClicked={hideDownloadButtons}
          showCancelLabel={formatSelectorVisible}
          redirectToSignUpLink={redirectToSignUpLink}
          detailsUrl={stockItem.detailsUrl}
        />
      </div>
    );
  }, [
    formatSelectorVisible,
    shouldShowDownloadButton,
    getPrimaryClickAction,
    hideDownloadButtons,
    redirectToSignUpLink,
    stockItem,
  ]);

  const renderVerticalVideoIcons = useCallback(
    (useIconRelativeButton: boolean = false) => {
      const shouldShowRelatedVideosButton = context === StockItemContext.SEARCH;

      return (
        <div
          className={`flex flex-col w-full absolute items-end left-0 bottom-0 px-4 pb-4 gap-y-2 ${
            !isHovering ? 'hidden' : ''
          }`}
        >
          {shouldShowAIEditButton && (
            <AIEditButton
              onClick={handleAIEditAction}
              disabled={isContentEditPolling}
            />
          )}
          <FavoriteButton
            containPoppers={containPoppers}
            stockItem={stockItem}
            stockItemFormats={stockItemFormats}
            favoriteButtonTelemetryAction={favoriteButtonTelemetryAction}
            openFoldersInNewWindow={openFoldersInNewWindow}
            onClick={handleClickAction}
            context={context}
            thumbnailExperiment
          />
          {shouldShowMakerButton && (
            <MakerButton
              stockItem={stockItem}
              makerButtonTelemetryAction={makerButtonTelemetryAction}
              thumbnailExperiment
            />
          )}
          <div
            className={`${
              shouldShowRelatedVideosButton ? 'w-full' : 'w-auto'
            } flex justify-between gap-y-2`}
          >
            {shouldShowRelatedVideosButton && (
              <RelatedVideosButtonContainer
                stockItem={stockItem}
                onClick={similarContentAction}
                onlyShowIcon={useIconRelativeButton}
              />
            )}
            <DownloadButton
              shouldShowDownloadButton={shouldShowDownloadButton}
              downloadButtonTelemetryAction={downloadButtonTelemetryAction}
              onClick={getPrimaryClickAction()}
              thumbnailExperiment
            />
          </div>
        </div>
      );
    },
    [
      context,
      stockItem,
      similarContentAction,
      handleAIEditAction,
      containPoppers,
      stockItemFormats,
      favoriteButtonTelemetryAction,
      openFoldersInNewWindow,
      handleClickAction,
    ]
  );

  const renderStockItemCard = useCallback(
    ({ useIconRelativeButton = false } = {}) => (
      <>
        <div className="box thumbnail z-0">
          <div
            className={twMerge(
              'thumbnail-img-wrapper overflow-hidden rounded-2xl',
              thumbnailClassNames
            )}
          >
            {formatSelectorVisible &&
              (useNewDownloadOverlay
                ? renderStockItemFormatSelectorThumbnailExperiment()
                : renderStockItemFormatSelector())}
            <StockItemIcons
              showNewFlag={shouldStockItemHaveNewFlag}
              className={`left-4 h-auto top-4 ${
                isHovering ? 'opacity-0' : 'opacity-100'
              }`}
              iconClassName={
                'bg-white px-1.5 py-1 leading-4 text-gray-900 text-sm'
              }
            />
            {draggableCard ? renderVideo() : renderVideoWithDetailsLink()}
            <LoadingIndicator show={isHovering && !isPlaying} />
          </div>
          <div
            className={twMerge(
              'absolute inset-0 flex items-center justify-center rounded-2xl pointer-events-none',
              isHovering ? 'opacity-100' : 'opacity-0',
              overlayGradientClassNames
            )}
            style={{
              background:
                'linear-gradient(180deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 40%)',
            }}
          ></div>
          {renderTitle()}
          {!useNewDownloadOverlay && renderDownloadCancelButtons()}
        </div>
        {isVertical ? (
          renderVerticalVideoIcons(useIconRelativeButton)
        ) : (
          <StockItemActions
            {...{
              isHovering,
              stockItem,
              stockItemFormats,
              context,
              containPoppers,
              favoriteButtonTelemetryAction,
              makerButtonTelemetryAction,
              downloadButtonTelemetryAction,
              openFoldersInNewWindow,
              similarContentAction,
              handleAIEditAction,
              handleClickAction,
              shouldShowAIEditButton,
              shouldShowMakerButton,
              shouldShowDownloadButton,
              useIconRelativeButton,
              getPrimaryClickAction,
            }}
          />
        )}
      </>
    ),
    [
      thumbnailClassNames,
      formatSelectorVisible,
      useNewDownloadOverlay,
      renderStockItemFormatSelectorThumbnailExperiment,
      renderStockItemFormatSelector,
      shouldStockItemHaveNewFlag,
      isHovering,
      draggableCard,
      renderVideo,
      renderVideoWithDetailsLink,
      isPlaying,
      overlayGradientClassNames,
      renderTitle,
      renderDownloadCancelButtons,
    ]
  );

  const renderCollapsedStockItemLoadMoreCard = useCallback(
    () => (
      <div
        onClick={(event) => {
          handleLoadingMoreCollapsedStockItems();
          handleStockItemClick(event);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            handleLoadingMoreCollapsedStockItems();
            handleStockItemClick(event);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div
          className={`box thumbnail faded load-more ${thumbnailClassNames} z-0`}
        >
          <StockItemIcons showNewFlag={shouldStockItemHaveNewFlag} />
          <img
            className="action-icon thumbnail-img"
            src={stockItem.thumbnailUrl}
            alt={stockItem.title}
            loading="eager"
          />
          <div className="controls">{renderStockItemTitle()}</div>
        </div>
        <h4 className="faded-thumbnail-text">Load More</h4>
      </div>
    ),
    [
      handleStockItemClick,
      thumbnailClassNames,
      shouldStockItemHaveNewFlag,
      stockItem,
      renderStockItemTitle,
    ]
  );

  // Main render
  return (
    <div
      className={getContainerClassNames()}
      data-testid="video-stock-item-card"
      data-stock-id={stockItem.id}
      ref={containerRef}
      onMouseEnter={handleIsHovering}
      onFocus={handleIsHovering}
      onMouseLeave={handleIsNotHovering}
      onBlur={handleIsNotHovering}
      style={style}
      itemType="schema.org/VideoObject"
    >
      {cardInViewport && isCollapsedStockItemLoadMoreCard
        ? renderCollapsedStockItemLoadMoreCard()
        : renderStockItemCard()}
    </div>
  );
};

export default VideoStockItemCard;
